import React from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight } from "lucide-react";

/**
 * About page — content sourced from ABOUT.docx.
 * Anchors: #clinical-content, #privacy, #community (linked from homepage footer).
 */
export default function About() {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-[#f5efe4] text-[#1a1612]" style={{ fontFamily: "'Georgia', 'Times New Roman', serif" }}>
      <style>{`
        .about-heading { font-family: 'Playfair Display', 'Georgia', serif; letter-spacing: -0.01em; }
        .about-sans { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; }
      `}</style>

      <header className="border-b border-[#e5dccb] bg-[#f5efe4]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-5 flex items-center justify-between">
          <button onClick={() => navigate("/")} className="flex items-center" data-testid="about-back">
            <img src="/project-rn-logo-horizontal.png" alt="Project RN" className="h-9 w-auto" />
          </button>
          <button
            onClick={() => navigate("/")}
            className="about-sans text-sm text-[#3a322b] hover:text-[#6f2a30] flex items-center gap-1.5"
          >
            <ArrowLeft className="h-3.5 w-3.5" /> Back
          </button>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 lg:px-10 py-14 space-y-14">
        <section>
          <div className="text-xs tracking-[0.25em] uppercase text-[#6f2a30] about-sans mb-4">About Project RN</div>
          <h1 className="about-heading text-5xl font-bold leading-tight" data-testid="about-title">Project RN</h1>
          <p className="about-sans mt-6 text-base text-[#3a322b] leading-relaxed">
            Project RN is an educational simulation and learning platform built by an experienced
            critical-care nurse for nurses who want to strengthen their clinical reasoning. The goal
            is simple: help nurses practice making decisions, understand the &ldquo;why&rdquo; behind
            critical-care concepts, and build confidence before those decisions matter at the bedside.
          </p>
          <p className="about-sans mt-4 text-base text-[#3a322b] leading-relaxed">
            Project RN is designed for education and simulation. It does not replace clinical training,
            institutional policies, established protocols, or the judgment of a qualified healthcare
            professional.
          </p>
        </section>

        <section id="clinical-content">
          <h2 className="about-heading text-2xl font-semibold mb-3">Clinical content</h2>
          <p className="about-sans text-sm text-[#3a322b] leading-relaxed">
            Scenarios, references, and teaching content are developed by an experienced critical-care
            nurse using established critical-care standards, clinical guidelines, and evidence-based
            references. Project RN is an educational resource&mdash;not a clinical decision-support
            system&mdash;and is not intended to direct the care of real patients.
          </p>
        </section>

        <section id="privacy">
          <h2 className="about-heading text-2xl font-semibold mb-3">Data &amp; privacy</h2>
          <p className="about-sans text-sm text-[#3a322b] leading-relaxed">
            Project RN is designed to be used without patient-identifiable information. Do not enter
            names, medical record numbers, dates of birth, or other protected patient information into
            simulations, conversations, or Code Scribe. Limited usage data may be collected to
            understand how Project RN is being used and to improve the experience. For additional
            information, please review the Project RN Privacy Policy.
          </p>
        </section>

        <section id="community">
          <h2 className="about-heading text-2xl font-semibold mb-3">Project RN Community</h2>
          <p className="about-sans text-sm text-[#3a322b] leading-relaxed">
            Better nurses are built through better questions. The Project RN community is a place to
            discuss clinical scenarios, explore bedside reasoning, suggest new learning topics, and
            connect with nurses committed to understanding the why&mdash;not simply memorizing the
            answer.
          </p>
          <a
            href="https://www.skool.com/projectrn-7210/about"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="about-community-cta"
            className="about-sans inline-flex items-center gap-1.5 mt-5 text-sm font-semibold text-[#6f2a30] hover:text-[#4a1a1e]"
          >
            Explore the Project RN Community <ArrowRight className="h-3.5 w-3.5" />
          </a>
        </section>
      </main>

      <footer className="border-t border-[#e5dccb] bg-[#f5efe4]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-6 about-sans text-xs text-[#6b6258]">
          Educational simulation only. Not medical advice.
        </div>
      </footer>
    </div>
  );
}
